/**
 * SM-2 spaced repetition algorithm (simplified variant).
 *
 * For each tracked item we keep:
 *   { wordId, attempts, correct, easeFactor, interval, nextReview, errorLog }
 *
 * `nextReview` is an ISO date string (YYYY-MM-DD).
 *
 * All functions here are pure: given the same input they return the same output
 * and never mutate the input record.
 */

import { SM2 } from '../data/constants.js';

/**
 * Format a Date as YYYY-MM-DD using local time.
 */
export function formatDateISO(date) {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Add N days to a Date and return a new Date object.
 */
export function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Create a fresh tracking record for a newly-seen item.
 *
 * `wrongPool` is the mistake-recap flag: it goes true when the child gets
 * the item wrong on the first attempt of a session, and clears the next
 * time they get it right on the first attempt. Used by both the regular
 * session composer and the dedicated "review mistakes" game.
 */
export function createTrackingRecord(wordId, now = new Date()) {
  return {
    wordId,
    attempts: 0,
    correct: 0,
    easeFactor: SM2.INITIAL_EASE,
    interval: 0,
    nextReview: formatDateISO(now),
    errorLog: [],
    wrongPool: false,
  };
}

/**
 * Apply the mistake-recap flag update.
 *
 *   - `firstAttemptCorrect === true`  → child nailed it on the first try
 *      → clear wrongPool.
 *   - `firstAttemptCorrect === false` → first attempt was wrong (regardless
 *      of whether they later recovered within the same question)
 *      → mark wrongPool.
 *   - `firstAttemptCorrect == null`   → unknown / not provided → leave as is
 *      (back-compat with callers that don't pass it).
 *
 * Pure: returns a new record, never mutates.
 */
export function applyMistakeFlag(record, firstAttemptCorrect) {
  if (firstAttemptCorrect === null || firstAttemptCorrect === undefined) {
    return record;
  }
  return {
    ...record,
    wrongPool: firstAttemptCorrect ? false : true,
  };
}

/**
 * Apply an SM-2 update to a tracking record.
 *
 * @param {object} record - existing tracking record (or undefined to create one)
 * @param {boolean} isCorrect - whether the answer was correct
 * @param {string[]} errorTags - tags from the item if answer was wrong (logged for analysis)
 * @param {string} wordId - the item id (used when record is fresh)
 * @param {Date} now - current date (injectable for tests)
 * @returns {object} - a new tracking record (never mutates input)
 */
export function applySm2(record, isCorrect, errorTags = [], wordId = null, now = new Date()) {
  const base = record ? { ...record } : createTrackingRecord(wordId ?? 'unknown', now);

  const attempts = base.attempts + 1;
  const correct = base.correct + (isCorrect ? 1 : 0);

  let newEase = base.easeFactor;
  let newInterval = base.interval;

  if (isCorrect) {
    newEase = Math.min(base.easeFactor + SM2.EASE_INCREMENT, 5.0);
    // Interval progression: 0 -> 1 -> 3 -> previous * ease
    if (base.interval === 0) {
      newInterval = 1;
    } else if (base.interval === 1) {
      newInterval = 3;
    } else {
      newInterval = Math.round(base.interval * newEase);
    }
  } else {
    newEase = Math.max(base.easeFactor - SM2.EASE_DECREMENT, SM2.MIN_EASE);
    newInterval = 1;
  }

  const nextReviewDate = addDays(now, newInterval);
  const errorLog = isCorrect
    ? base.errorLog
    : [...base.errorLog, ...(errorTags || [])].slice(-50); // cap history

  return {
    ...base,
    attempts,
    correct,
    easeFactor: Number(newEase.toFixed(2)),
    interval: newInterval,
    nextReview: formatDateISO(nextReviewDate),
    errorLog,
  };
}

/**
 * Check whether a tracking record is due for review on the given date.
 */
export function isDue(record, today = new Date()) {
  if (!record) return false;
  const todayStr = formatDateISO(today);
  return record.nextReview <= todayStr;
}

/**
 * Return only the items from `allItems` that are due for review.
 * `trackingMap` is { wordId: trackingRecord }
 */
export function getDueItems(allItems, trackingMap, today = new Date()) {
  return allItems.filter((item) => {
    const record = trackingMap[item.id];
    return record && isDue(record, today);
  });
}

/**
 * Return items that have never been seen (no tracking record).
 */
export function getUnseenItems(allItems, trackingMap) {
  return allItems.filter((item) => !trackingMap[item.id]);
}
